import { constants } from '@pukka/common';
import { DeepReadonly } from 'ts-essentials';
import { z } from 'zod';

const { PROD } = constants;

function getFeatureFlagSchema(isProd: boolean) {
  return z.object({
    description: z.string(),
    enabled: z.boolean(),
    expiry: !isProd ? z.date().min(new Date(), { message: 'flag has expired' }) : z.date(),
    author: z.array(!isProd ? z.string().email() : z.string()).nonempty(),
    variant: z
      .object({
        name: z.string(),
        enabled: z.boolean(),
        payload: z
          .object({
            type: z.string(),
            value: z.string(),
          })
          .optional(),
      })
      .optional(),
    cleanupTicket: z.string().optional(), // please change this to mandatory
  });
}

const featureFlagSchema = getFeatureFlagSchema(PROD);
const featureFlagNames = z.enum([
  'backoffice-bundle-report-red-dot',
  'bundle-package-red-dot-ui',
  'ecommerce-attributes',
  'enable-gobiz-outlet-linking',
  'recurring-payment-backoffice',
  'enable-whitelist-all-business-category',
  'enable-shopping-ext',
  'enable-google-shopping-ext',
  'enable-facebook-shopping-ext',
  'backoffice-pukka-redirection',
  'marketing-modal-backoffice',
  'marketing-modal-backoffice-2',
  'marketing-modal-backoffice-3',
  'backoffice-multi-language',
  'backoffice-principal-brands',
  'backoffice-download-moka-pc',
  'backoffice-enable-gopay-registration',
] as const);

type FlagNames = z.infer<typeof featureFlagNames>;
const featureFlagSchemas = z.record(featureFlagNames, featureFlagSchema);
type FeatureFlagConfig = DeepReadonly<Required<z.infer<typeof featureFlagSchemas>>>;
const flags: FeatureFlagConfig = featureFlagSchemas.parse({
  'enable-whitelist-all-business-category': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'enable-shopping-ext': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'enable-google-shopping-ext': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'enable-facebook-shopping-ext': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'backoffice-bundle-report-red-dot': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'bundle-package-red-dot-ui': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'ecommerce-attributes': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'enable-gobiz-outlet-linking': {
    description: 'Shows the new create custom discount UI as well as show of custom discounts in discount list',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['yunlong.chong@gojek.com'],
  },
  'recurring-payment-backoffice': {
    description: 'Shows the recurring payment feature',
    enabled: false,
    expiry: new Date(2035, 10, 1),
    author: ['aulia.nurrahmann@gojek.com'],
  },
  'backoffice-pukka-redirection': {
    description: 'Handles the redirection in one config',
    enabled: true,
    expiry: new Date(2035, 2, 1),
    author: ['andree.christian@gojek.com'],
  },
  'marketing-modal-backoffice': {
    description: 'Handles the marketing modal',
    enabled: true,
    expiry: new Date(2035, 2, 1),
    author: ['andree.christian@gojek.com'],
  },
  'marketing-modal-backoffice-2': {
    description: 'Handles the marketing modal',
    enabled: true,
    expiry: new Date(2035, 2, 1),
    author: ['andree.christian@gojek.com'],
  },
  'marketing-modal-backoffice-3': {
    description: 'Handles the marketing modal',
    enabled: true,
    expiry: new Date(2035, 2, 1),
    author: ['andree.christian@gojek.com'],
  },
  'backoffice-multi-language': {
    description: 'Enable multi language support',
    enabled: true,
    expiry: new Date(2035, 6, 1),
    author: ['aulia.nurrahman@gojek.com'],
  },
  'backoffice-principal-brands': {
    description: 'Enable principal brands',
    enabled: true,
    expiry: new Date(2026, 2, 1),
    author: ['aulia.nurrahman@gojek.com'],
  },
  'backoffice-download-moka-pc': {
    description: 'Enable download moka pc',
    enabled: true,
    expiry: new Date(2035, 12, 1),
    author: ['aulia.nurrahman@gojek.com'],
  },
  'backoffice-enable-gopay-registration': {
    description: 'Enable gopay registration',
    enabled: true,
    expiry: new Date(2026, 12, 1),
    author: ['aulia.nurrahman@gojek.com'],
  },
} satisfies FeatureFlagConfig) as unknown as FeatureFlagConfig; // we need to do this as featureFlagSchemas returns `Partial`

export type { FlagNames, FeatureFlagConfig };

export { flags, featureFlagNames, featureFlagSchemas, getFeatureFlagSchema as _getFeatureFlagSchema };
